import { useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";

import { requestPasswordReset } from "@/auth/api";
import { AuthError, AuthNotice, AuthScreen } from "@/auth/AuthScreen";
import { Button, FormField, Input } from "@/components/ui";

/**
 * "I forgot my password": ask for the email that carries the reset link.
 *
 * The confirmation never says whether the address has an account. allauth
 * answers the same either way so that this form cannot be used to find out
 * who works here, and a screen that said "sent!" only for real accounts would
 * hand that straight back.
 */
export function PasswordRecovery() {
  const { t } = useTranslation();
  const location = useLocation();
  const [email, setEmail] = useState(
    (location.state as { email?: string } | null)?.email ?? "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const address = email.trim();
    setBusy(true);
    setError(null);
    const result = await requestPasswordReset(address);
    setBusy(false);
    if (result.ok) setSentTo(address);
    else setError(result.message);
  }

  return (
    <AuthScreen
      title={t("auth.recovery.title")}
      footer={
        <Link to="/entrar" className="btn btn-ghost">
          {t("auth.recovery.back")}
        </Link>
      }
    >
      {sentTo ? (
        <AuthNotice>{t("auth.recovery.sent", { email: sentTo })}</AuthNotice>
      ) : (
        <>
          <p className="mb-4 text-sm text-mute-700">{t("auth.recovery.intro")}</p>
          {error ? <AuthError>{error}</AuthError> : null}
          <form onSubmit={submit} className="space-y-4">
            <FormField label={t("auth.login.email")}>
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  name="email"
                  autoComplete="username"
                  autoFocus
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              )}
            </FormField>
            <Button variant="primary" type="submit" block disabled={busy}>
              {busy ? t("auth.recovery.submitting") : t("auth.recovery.submit")}
            </Button>
          </form>
        </>
      )}
    </AuthScreen>
  );
}
